import { useState } from 'react'
import { rosterShift } from '../data/roster-shifts'
import type { IsoDate, ShiftCode } from '../engine/types'
import { AD_MESSAGES } from '../lib/ads'
import type { AdCallResult, AdIntent } from '../lib/ads'
import { formatShortDate, formatTimeRange } from '../lib/format-date'
import { Button, SegmentedControl, Sheet, TextField, Toggle } from '../ui'
import type { SegmentedOption } from '../ui'
import { ShiftCodeChip } from './ShiftCodeChip'

/** The shift being advertised: date + code always; `shiftId` only when the row already exists (an FWA entry). A derived block shift has no row yet — advertising it is what materialises one. */
export interface AdvertiseTarget {
  date: IsoDate
  code: ShiftCode
  shiftId?: string
}

export interface AdvertiseSheetProps {
  target: AdvertiseTarget
  /**
   * `ads.ts`'s `createAdvertisement`, owned by the screen — the typed result
   * comes back so this sheet renders rejections as sentences, the
   * `RosterDataError` idiom. The screen refetches on success.
   */
  onAdvertise: (draft: {
    noNightSwap: boolean
    intent: AdIntent
    note: string | null
  }) => Promise<AdCallResult>
  onClose: () => void
}

const INTENT_OPTIONS: SegmentedOption<AdIntent>[] = [
  { value: 'OPEN_TO_SWAP', label: 'Open to swap' },
  { value: 'NEED_OFF', label: 'Needs the day off' },
]

const INTENT_HINT =
  'Display only, for now — either way the swap itself is settled as one shift for one shift when an offer is accepted.'

/**
 * The advertise composer, inside the vendored `Sheet` (plan §1 Client, D2's
 * honest labels). Shows the shift with its real roster times, takes the two
 * flags and an optional note, and submits through `onAdvertise` — the sheet
 * owns the sentences, the screen owns `src/lib/ads.ts`, exactly the split
 * `DaySheet` established in Phase 3.
 *
 * The NS flag (B7) is recorded and shown in this phase; *enforcement* —
 * excluding night-shift candidates — is Phase 5's, re-checked at confirm in
 * Phase 6. The toggle says what it does, not what it will do.
 */
export function AdvertiseSheet({ target, onAdvertise, onClose }: AdvertiseSheetProps) {
  const [noNightSwap, setNoNightSwap] = useState(false)
  const [intent, setIntent] = useState<AdIntent>('OPEN_TO_SWAP')
  const [noteText, setNoteText] = useState('')
  const [status, setStatus] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const times = rosterShift(target.code)

  async function handleSubmit() {
    if (submitting) return
    setSubmitting(true)
    const note = noteText.trim() === '' ? null : noteText.trim()
    const result = await onAdvertise({ noNightSwap, intent, note })
    setSubmitting(false)

    if (result.ok) {
      setStatus('Advertised — colleagues on other blocks can see it in their feed now.')
      return
    }
    if (result.rejection !== null) {
      setStatus(AD_MESSAGES[result.rejection])
      return
    }
    setStatus(`Couldn't advertise — ${result.error.message}`)
  }

  return (
    <Sheet title={`Offer ${formatShortDate(target.date)} for swap`} onClose={onClose}>
      <div className="sl-stack">
        <p className="ss-sheet-status" role="status">
          {status}
        </p>

        <div className="ss-sheet-section">
          <div className="ss-day-sheet__shift">
            <ShiftCodeChip code={target.code} />
            <span className="ss-day-sheet__shift-times">
              {formatTimeRange(times.startMin, times.endMin)}
            </span>
          </div>
          <p className="sl-hint">
            Advertising makes this shift public to every active colleague — that is the point.
            Nobody sees anything else of yours.
          </p>
        </div>

        <div className="ss-sheet-section">
          <SegmentedControl<AdIntent>
            label="Why"
            options={INTENT_OPTIONS}
            value={intent}
            onChange={setIntent}
            hint={INTENT_HINT}
          />
          <Toggle
            label="Will not swap for a night"
            checked={noNightSwap}
            onChange={setNoNightSwap}
            description="Shown on the card. Candidates offering an N in return are filtered out from Phase 5."
          />
          <TextField
            label="Note (optional)"
            value={noteText}
            onChange={setNoteText}
            hint="Shown on the card — e.g. what you'd take in return."
          />
        </div>

        <Button block onClick={handleSubmit} disabled={submitting}>
          Advertise this shift
        </Button>
      </div>
    </Sheet>
  )
}
